import { Children, createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import FeatherIcon from 'feather-icons-react';
import { useClickOutside } from 'hooks/click-outside';
import { useCombinedRefs } from 'hooks/combined-refs';
import PropTypes from 'prop-types';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { ChangeEvent } from 'utils/form-utils';

import { ValidationAlert } from 'components/FormComponents/ValidationAlert/ValidationAlert';

import styles from './Select.module.scss';

const SelectContext = createContext(null);

const useSelectContext = () => {
  const context = useContext(SelectContext);
  if (!context)
    throw new Error(`Select compound components cannot be rendered outside the Select component`);
  return context;
};

export const Select = ({
  name,
  value,
  placeholder = '',
  defaultValue,
  useDefault = false,
  useSearch = false,
  validators = {},
  register = (...args) => ({ ref: () => {} }),
  setValue,
  disabled,
  nullTag,
  children,
  className,
  inputProps = {},
  onChange,
  error,
  noErrorMessage,
  rounded = false,
  small = false,
  /** When set, controls the closed trigger content (dropdown options unchanged). Return `undefined` to use default label. */
  renderButtonLabel,
  selectIcon = null,
  inLineSelect = false,
  dataTestId,
}) => {
  const { ref, ...registerProps } = register(name, validators);
  const inputRef = useRef(null);
  const combinedRef = useCombinedRefs(ref, inputRef);

  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const wrapperRef = useRef(null);

  useClickOutside(wrapperRef, () => setShowMenu(false));

  const [options, setOptions] = useState([]);
  const [optionLabel, setOptionLabel] = useState();
  const [searchQuery, setSearchQuery] = useState('');
  const [showMenu, setShowMenu] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [focusedValue, setFocusedValue] = useState(null);
  const [announcement, setAnnouncement] = useState('');

  // Store options
  useEffect(() => {
    setOptions(
      Children.toArray(children).map(child => ({
        label: child.props.children,
        value: child.props.value,
      })),
    );
  }, [children]);

  // Set label
  useEffect(() => {
    if (options.length < 1) setOptionLabel('');

    if (value === null) {
      setOptionLabel(nullTag);
    } else {
      const option = options.find(option => option.value == value);
      setOptionLabel(option?.label);
    }
  }, [value, options]);

  // Handle default value / first option
  useEffect(() => {
    if (!options.length) return;

    const currentValue = value;

    if (defaultValue && !currentValue) {
      if (setValue && name) {
        setValue(name, defaultValue, { shouldValidate: true, shouldTouch: true });
      } else {
        setOption(defaultValue);
      }
      return;
    }

    if (useDefault && !currentValue && options.length > 0) {
      if (options[0].value === currentValue) return;
      if (setValue && name) {
        setValue(name, options[0].value, { shouldValidate: true, shouldTouch: true });
      } else {
        setOption(options[0].value);
      }
    }
  }, [options, value, defaultValue, useDefault, name, setValue]);

  const setOption = selectedValue => {
    setShowMenu(false);
    setFocusedIndex(-1);
    setFocusedValue(null);

    const selectedOption = options.find(option => option.value == selectedValue);
    if (selectedOption) {
      setAnnouncement(`Selected ${selectedOption.label}`);
    }

    const finalOnChange = onChange || inputProps?.onChange || registerProps?.onChange;
    if (finalOnChange) {
      finalOnChange(ChangeEvent(name, selectedValue));
    }
  };

  const contextValue = useMemo(
    () => ({ setOption, searchQuery, focusedIndex, setFocusedIndex, focusedValue, name }),
    [setOption, searchQuery, focusedIndex, focusedValue, name],
  );

  const handleQueryChange = e => setSearchQuery(e.target.value.toLowerCase());

  const visibleOptions = useMemo(() => {
    return Children.toArray(children).filter(child => {
      if (!child) return false;
      const label = child.props.children;
      return (label != null ? String(label) : '').toLowerCase().includes(searchQuery.toLowerCase());
    });
  }, [children, searchQuery]);

  // Helpers for open/close
  const openMenu = () => setShowMenu(true);
  const closeMenu = () => setShowMenu(false);

  // Keyboard navigation
  const handleKeyDown = e => {
    if (disabled) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!showMenu) return openMenu();
        setFocusedIndex(prev => {
          const next = (prev + 1) % visibleOptions.length;
          setFocusedValue(visibleOptions[next].props.value);
          return next;
        });
        break;

      case 'ArrowUp':
        e.preventDefault();
        if (!showMenu) return openMenu();
        setFocusedIndex(prev => {
          const next = (prev - 1 + visibleOptions.length) % visibleOptions.length;
          setFocusedValue(visibleOptions[next].props.value);
          return next;
        });
        break;

      case 'Enter':
      case ' ':
        e.preventDefault();
        if (showMenu && focusedValue) setOption(focusedValue);
        else openMenu();
        break;

      case 'Escape':
      case 'Tab':
        closeMenu();
        break;
    }
  };

  // Focus management - only focus when user interacts, not on mount
  useEffect(() => {
    if (showMenu) menuRef.current?.focus();
  }, [showMenu]);

  // Announce changes to screen readers
  useEffect(() => {
    if (announcement) {
      const timer = setTimeout(() => setAnnouncement(''), 1000);
      return () => clearTimeout(timer);
    }
  }, [announcement]);

  return (
    <SelectContext.Provider value={contextValue}>
      <div
        ref={wrapperRef}
        className={cn(styles.root, {
          [styles.rootOpen]: showMenu,
          [className]: className,
          [styles.inlineSelect]: inLineSelect,
        })}>
        <input
          name={name}
          ref={combinedRef}
          value={value || ''}
          onChange={() => {}}
          {...registerProps}
          {...inputProps}
        />
        <button
          ref={buttonRef}
          type="button"
          className={cn(styles.button, {
            [styles.disabled]: disabled,
            [styles.rounded]: rounded,
            [styles.small]: small,
            [styles.invalid]: !!error,
          })}
          onClick={() => !disabled && setShowMenu(!showMenu)}
          onKeyDown={handleKeyDown}
          aria-expanded={showMenu}
          aria-haspopup="listbox"
          aria-labelledby={`${name}-label`}
          aria-describedby={error ? `${name}-error` : undefined}
          disabled={disabled}
          tabIndex={0}
          data-testid={dataTestId}>
          <span className={cn(styles.valueWrapper, 'u-text-ellipsis')}>
            <div className="d-flex flex-row u-gap-small align-items-center">
              {selectIcon && <HugeiconsIcon icon={selectIcon} size="20" strokeWidth="1.5" />}
              <span className="u-text-ellipsis">
                {(() => {
                  let display =
                    typeof renderButtonLabel === 'function'
                      ? renderButtonLabel(value, optionLabel)
                      : undefined;
                  if (display === undefined) display = optionLabel;
                  if (display == null || display === false) {
                    return <span className={styles.placeholder}>{placeholder}</span>;
                  }
                  return display;
                })()}
              </span>
            </div>
          </span>
        </button>
        <div
          ref={menuRef}
          className={cn(styles.menu, { [styles.menuVisible]: showMenu })}
          role="listbox"
          aria-labelledby={`${name}-label`}
          aria-activedescendant={focusedValue ? `${focusedValue}-option` : undefined}
          style={{ display: showMenu ? 'block' : 'none' }}
          onKeyDown={handleKeyDown}
          tabIndex={-1}>
          {useSearch && (
            <div className={cn(styles.search)}>
              <FeatherIcon className={styles.icon} icon="search" />
              <input
                type="text"
                value={searchQuery}
                onChange={handleQueryChange}
                className={styles.input}
                placeholder="Search..."
                aria-label="Search options"
              />
            </div>
          )}
          <ul role="presentation">
            {nullTag && (
              <>
                <li className={cn(styles.item, styles.nullTag)} onClick={() => setOption(null)}>
                  {nullTag}
                </li>
              </>
            )}
            {Children.map(children, (child, index) =>
              child ? { ...child, props: { ...child.props, index } } : child,
            )}
          </ul>
        </div>
        {error && !noErrorMessage && (
          <div id={`${name}-error`} className="mt-2" role="alert">
            <ValidationAlert show={true} message={error} />
          </div>
        )}
        {announcement && (
          <div aria-live="polite" aria-atomic="true" className="sr-only">
            {announcement}
          </div>
        )}
      </div>
    </SelectContext.Provider>
  );
};

Select.propTypes = {
  name: PropTypes.string,
  value: PropTypes.any,
  placeholder: PropTypes.string,
  defaultValue: PropTypes.any,
  useDefault: PropTypes.bool,
  useSearch: PropTypes.bool,
  validators: PropTypes.object,
  register: PropTypes.func,
  setValue: PropTypes.func,
  disabled: PropTypes.bool,
  children: PropTypes.node,
  className: PropTypes.string,
  inputProps: PropTypes.object,
  error: PropTypes.string,
  noErrorMessage: PropTypes.bool,
  renderButtonLabel: PropTypes.func,
};

const Item = ({ value, children, className, description, index, testId }) => {
  const { setOption, searchQuery, focusedValue, name } = useSelectContext();
  const itemRef = useRef(null);

  const [filtered, setFiltered] = useState(false);
  const isFocused = focusedValue === value;

  const handleSelected = () => setOption(value);

  useEffect(() => {
    const label = children != null ? String(children) : '';
    setFiltered(label.toLowerCase().indexOf(searchQuery) < 0);
  }, [children, searchQuery]);

  useEffect(() => {
    if (isFocused && itemRef.current && !filtered) {
      itemRef.current.focus();
    }
  }, [isFocused, filtered]);

  return (
    <li
      ref={itemRef}
      role="option"
      id={`${value}-option`}
      className={cn(styles.item, {
        [styles.itemHidden]: filtered,
        [className]: className,
        [styles.focused]: isFocused && !filtered,
      })}
      onClick={handleSelected}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleSelected();
        }
      }}
      tabIndex={-1}
      aria-selected={isFocused && !filtered}
      data-testid={testId ?? (name ? `select-option-${name}-${value}` : undefined)}>
      {children}
      {description && (
        <p className={cn(styles.itemDescription, 'text-text-tertiary text-lg font-regular my-1')}>
          {description}
        </p>
      )}
    </li>
  );
};

Item.propTypes = {
  value: PropTypes.any,
  children: PropTypes.node,
  className: PropTypes.string,
  description: PropTypes.string,
  index: PropTypes.number,
  testId: PropTypes.string,
};

Select.Item = Item;
